import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/plantusTestIds";
import { PaperPlaneTilt, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte füllen Sie Name, E-Mail und Nachricht aus.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Vielen Dank! Wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 sm:py-32 bg-bone-100/70"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Left: info */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta mb-3">
            Kontakt
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest-900 tracking-tight">
            Sprechen Sie mit dem Plantus-Team.
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-[#4A524C] max-w-md">
            Fragen zur Umsetzung der EU&nbsp;2023/564 in Ihrem Betrieb? Interesse
            an einem Pilot? Wir freuen uns auf Ihre Nachricht.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0">
                <EnvelopeSimple size={20} weight="duotone" className="text-forest-700" />
              </div>
              <div>
                <div className="font-heading text-sm font-semibold text-forest-900">E-Mail</div>
                <a href="mailto:info@plantus-app.com" className="font-body text-[15px] text-[#4A524C] hover:text-forest-700">
                  info@plantus-app.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0">
                <Phone size={20} weight="duotone" className="text-forest-700" />
              </div>
              <div>
                <div className="font-heading text-sm font-semibold text-forest-900">Telefon</div>
                <span className="font-body text-[15px] text-[#4A524C]">Mo,Fr · 8:00-17:00</span>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0">
                <MapPin size={20} weight="duotone" className="text-forest-700" />
              </div>
              <div>
                <div className="font-heading text-sm font-semibold text-forest-900">Standort</div>
                <span className="font-body text-[15px] text-[#4A524C]">Deutschland · Europa kommt</span>
              </div>
            </li>
          </ul>
        </div>

        {/* Right: form */}
        <div className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8 lg:p-10 shadow-[0_1px_2px_rgba(20,30,20,0.04),0_10px_40px_-12px_rgba(20,30,20,0.15)]">
          {sent ? (
            <div
              data-testid={CONTACT.success}
              className="h-full flex flex-col items-center justify-center text-center py-10"
            >
              <div className="w-14 h-14 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center mb-5">
                <PaperPlaneTilt size={26} weight="duotone" className="text-forest-700" />
              </div>
              <h3 className="font-heading text-2xl font-medium text-forest-900">
                Nachricht gesendet
              </h3>
              <p className="mt-3 font-body text-[15px] text-[#4A524C] max-w-sm">
                Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 text-sm font-medium text-terracotta hover:underline"
              >
                Weitere Nachricht senden
              </button>
            </div>
          ) : (
            <form data-testid={CONTACT.form} onSubmit={onSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testId={CONTACT.name}
                  required
                />
                <Field
                  label="E-Mail"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  testId={CONTACT.email}
                  required
                />
              </div>
              <Field
                label="Organisation (optional)"
                name="organisation"
                value={form.organisation}
                onChange={onChange}
                testId={CONTACT.org}
              />
              <div>
                <label className="block font-body text-sm font-medium text-forest-900 mb-2">
                  Ihre Nachricht
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  data-testid={CONTACT.message}
                  className="w-full font-body text-[15px] px-4 py-3 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition resize-none"
                  placeholder="Worum geht es?"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid={CONTACT.submit}
                className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3.5 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                <PaperPlaneTilt size={18} weight="bold" />
                {loading ? "Senden …" : "Nachricht senden"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testId,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-2">
      {label}
    </label>
    <input
      data-testid={testId}
      className="w-full font-body text-[15px] px-4 py-3 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    />
  </div>
);
